import React from 'react';

import {assert} from 'Asserts';
import {
  ProcedureType,
  ProcessClientOrigin,
  ProcessExecutionType,
  ProcessFlowchartNodeDetailsType,
  ProcessSingletonTaskSchemaName,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import DocumentActions from 'notebook/documents/DocumentActions';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  composeProcedureTypeMetadata,
  PROCEDURE_SAVE_STARTED_EVENT,
} from 'performance/processProceduresEvents';
import {getDocumentVersionUpdatesFromFlowchartConfig} from 'procedures/procedurePagePersistenceUtils';
import {TaskDefinitionVersionSpec} from 'processes/dao/ProcessFlowchartConfigDAO';
import {
  FlowchartConfigWithMetadataAndRootTaskSchema,
  ProcessFlowchartAdapter,
  ProcessFlowchartAdditionalPersistedData,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartAdapter';
import {NodeCreationContext} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import {isTaskNodeDetailsGQL} from 'processes/graph-builder-processes/flowchart-viz/util/getNodeDetailsTypeForGQL';
import {
  NodeSettingsTabKeys,
  STANDARD_END_NODE_ICON,
  STANDARD_END_NODE_NAME,
  STANDARD_ROOT_NODE_ICON,
  STANDARD_ROOT_NODE_NAME,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {ProcessTaskDefinitionVersionGraphQL} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  TaskDefinitionVersionSpecWithTaskSchema,
  TaskSchemaNodeSectionData,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {ProcessFlowchartConfigForEditorWithLatestVersionGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessFlowchartConfigForEditorWithLatestVersionGraphQL';
import {ProcessTaskSchemaGraphQL} from '../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';

interface ValuableProcedureServerData {
  stepIdToDocumentVersionIdMap: Record<string, string | null>;
}

function TemporaryRootNodeSidepanelContent(): JSX.Element {
  return <div>Temporary Root node Sidepanel Content</div>;
}

export default class ProcedureRootNodeAdapter extends ProcessFlowchartAdapter<
  null,
  null,
  ValuableProcedureServerData
> {
  readonly executionType: ProcessExecutionType = ProcessExecutionType.PROCEDURE;
  readonly typeDisplayName: string = 'procedure';

  nodeDetailsSidepanelContent = [
    {
      key: NodeSettingsTabKeys.FLOWCHART_SETTINGS,
      label: 'Start node',
      content: TemporaryRootNodeSidepanelContent,
    },
  ];

  convertTaskDefinitionVersionGQLToApplicationData(
    taskDefinitionVersionGQL: ProcessTaskDefinitionVersionGraphQL
  ): null {
    return null;
  }

  convertTaskDefinitionVersionSpecToNetworkApplicationData(
    taskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema<null>,
    flowchartConfigId: string
  ): null {
    return null;
  }

  getNodeName(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema,
    isFlowchartOutput?: boolean
  ): string {
    if (isFlowchartOutput === true) {
      return STANDARD_END_NODE_NAME;
    } else {
      return STANDARD_ROOT_NODE_NAME;
    }
  }

  getNodeSubtitle(taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema): string | null {
    return null;
  }

  getNodeIcon(
    taskDefinitionVersion: TaskDefinitionVersionSpec,
    isFlowchartOutput?: boolean
  ): BenchlingIconName {
    if (isFlowchartOutput === true) {
      return STANDARD_END_NODE_ICON;
    } else {
      return STANDARD_ROOT_NODE_ICON;
    }
  }

  createSections(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema,
    nodeDetailsType: ProcessFlowchartNodeDetailsType
  ): Array<TaskSchemaNodeSectionData> {
    return [];
  }

  onCreateNewNode(
    context: NodeCreationContext
  ): Promise<TaskDefinitionVersionSpecWithTaskSchema<null> | null> {
    assert(false, 'Procedure root nodes should not be created');
  }

  getNewNodeDetails(context: NodeCreationContext): {
    nodeName: string;
    nodeSubtitle: string | null;
    nodeIcon: BenchlingIconName | null;
  } {
    assert(false, 'Procedure root nodes should not be created');
  }

  getSingletonTaskSchemaName(
    rootTaskSchema: ProcessTaskSchemaGraphQL
  ): ProcessSingletonTaskSchemaName | null {
    return null;
  }

  onSaveStart(flowchartConfig: FlowchartConfigWithMetadataAndRootTaskSchema): void {
    const clientOrigin = flowchartConfig.metadata.rootTaskSchema.clientOrigin;
    let procedureType!: ProcedureType;
    if (clientOrigin === ProcessClientOrigin.RECIPES) {
      procedureType = ProcedureType.RECIPE;
    } else if (clientOrigin === ProcessClientOrigin.ASSAYS) {
      procedureType = ProcedureType.ASSAY;
    } else {
      assert(false, 'Unsupported client origin', {clientOrigin});
    }

    const eventMetadata = composeProcedureTypeMetadata(procedureType);
    logProfilerTimingEvent(PROCEDURE_SAVE_STARTED_EVENT, eventMetadata);

    const documentVersionUpdates = getDocumentVersionUpdatesFromFlowchartConfig(flowchartConfig);
    DocumentActions.notifySaveStart(documentVersionUpdates);
  }

  /**
   * After a successful save, extract valuable data from the server response. In this case, we extract a mapping of
   * step IDs to their associated document version IDs for all steps in the procedure, as the DVIDs are not otherwise
   * present on the cid-id map, or elsewhere on the client or in the server response.
   *
   * These DVIDs need to be installed after a save to ensure we're up to date with the server changes.
   *
   * @param serverResponse - The server response containing the latest flowchart config data.
   * @returns An object containing the mapping of step IDs to document version IDs.
   */
  override extractValuableDataFromServerResponse(
    serverResponse: ProcessFlowchartConfigForEditorWithLatestVersionGraphQL
  ): ValuableProcedureServerData {
    const allSteps = (serverResponse.flowchartConfigVersion?.templateFlowchart?.nodeConfigs ?? [])
      .map((node) =>
        isTaskNodeDetailsGQL(node.nodeDetails) ? node.nodeDetails.processTaskDefinitionVersion : null
      )
      .map((ptdv) =>
        ptdv &&
        !isInaccessible(ptdv) &&
        ptdv.applicationData?.__typename === 'ProcedureMethodDefinitionVersionNode'
          ? ptdv.applicationData
          : null
      )
      .filter((methodNode): methodNode is NonNullable<typeof methodNode> => methodNode != null)
      .map((methodNode) => methodNode.stepGroups.flatMap((sg) => sg.steps));

    const stepIdToDocumentVersionIdMap: Record<string, string | null> = {};
    for (const step of allSteps.flat()) {
      if (step.id != null) {
        stepIdToDocumentVersionIdMap[step.id] = step.documentVersion?.id ?? null;
      }
    }

    return {stepIdToDocumentVersionIdMap};
  }

  onSaveComplete(
    persistedFlowchartConfig: FlowchartConfigWithMetadataAndRootTaskSchema,
    additionalPersistedData: ProcessFlowchartAdditionalPersistedData & {
      valuableServerData: ValuableProcedureServerData;
    }
  ): Promise<FlowchartConfigWithMetadataAndRootTaskSchema> {
    const documentVersionUpdates = getDocumentVersionUpdatesFromFlowchartConfig(persistedFlowchartConfig);
    DocumentActions.notifySaveComplete(documentVersionUpdates, additionalPersistedData.cidToIdMap);
    // TODO(procedures): Incorporate cidToIdMap into persisted flowchart config to build new draft
    return Promise.resolve(persistedFlowchartConfig);
  }
}
